/**
 * WebRTC Service for Peer-to-Peer Communication
 * Handles direct canvas updates and permission management between host and guests
 */

class WebRTCService {
  constructor () {
    this.localConnection = null
    this.remoteConnection = null
    this.dataChannel = null
    this.canvasChannel = null
    this.permissionChannel = null
    this.zoomChannel = null
    this.isHost = false
    this.isConnected = false
    this.roomId = null
    this.socket = null
    this.pendingIceCandidates = []
    this.localIceCandidates = []
    this.connectionData = null

    // Signaling state tracking to prevent duplicates
    this.signalingState = 'new' // new, offer-sent, offer-received, answer-sent, answer-received, connected
    this.processedOffers = new Set()
    this.processedAnswers = new Set()

    // Event callbacks
    this.onCanvasUpdate = null
    this.onPermissionUpdate = null
    this.onConnectionStateChange = null
    this.onMessage = null
    this.onZoomUpdate = null
    this.onIceCandidate = null
    this.onOfferReady = null
    this.onAnswerReady = null
    this.onTrack = null // Callback for remote track addition
    this.onTrackEnded = null // Callback when a local media track ends (device lost/revoked)

    // Reconnection / renegotiation state
    this._reconnectTimer = null // pending recovery timer (grace window before ICE-restart)
    this._reconnectAttempts = 0
    this._offerGeneration = 0 // monotonic id so an ICE-restart offer is never deduped away
    this.iceTransportPolicy = null // set to 'relay' to force TURN on a retry

    // ICE servers configuration — default fallback only. Real (TURN-enabled)
    // servers are fetched from GET /online/turn-credentials and injected via
    // setIceServers() before initializeAsHost/Guest (P0-1).
    this.iceServers = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  }

  /**
   * Override the ICE servers (STUN/TURN) before initializing a connection.
   * Must be called before initializeAsHost/initializeAsGuest to take effect.
   */
  setIceServers (servers) {
    if (Array.isArray(servers) && servers.length) {
      this.iceServers = servers
    }
  }

  /**
   * Force the ICE transport policy (e.g. 'relay' to require TURN) before a
   * (re)connection. Used by the guest's relay-preferred retry once real TURN
   * exists — a direct path that keeps failing is skipped in favor of the relay.
   */
  setIceTransportPolicy (policy) {
    this.iceTransportPolicy = policy || null
  }

  /**
   * Build the RTCConfiguration, honoring an optional forced transport policy.
   */
  _rtcConfig () {
    const config = { iceServers: this.iceServers }
    if (this.iceTransportPolicy) config.iceTransportPolicy = this.iceTransportPolicy
    return config
  }

  /**
   * Initialize as Host
   */
  async initializeAsHost (roomId, socket) {
    console.log('🎯 Initializing WebRTC as Host for room:', roomId)
    this.isHost = true
    this.roomId = roomId
    this.socket = socket

    try {
      // Create RTCPeerConnection
      this.localConnection = new RTCPeerConnection(this._rtcConfig())

      // Create data channels for different types of communication
      this.setupDataChannels()

      // Set up connection event listeners
      this.setupConnectionEventListeners()

      // Set up socket signaling
      this.setupSocketSignaling()

      console.log('✅ Host WebRTC initialized successfully')
      return true
    } catch (error) {
      console.error('❌ Failed to initialize host WebRTC:', error)
      return false
    }
  }

  addTracks (stream) {
    if (stream && this.localConnection) {
      console.log('WEBRTC SERVICE ADDING TRACKS')
      stream.getTracks().forEach(track => {
        console.log('Adding track:', track)
        this.localConnection.addTrack(track, stream)
        // Detect a device that is unplugged / revoked mid-call so the view can
        // re-acquire it and renegotiate instead of the peer seeing a frozen frame.
        track.onended = () => {
          console.warn(`⚠️ Local ${track.kind} track ended`)
          if (this.onTrackEnded) this.onTrackEnded(track)
        }
      })
    }
  }
  /**
   * Rebuild the underlying RTCPeerConnection while KEEPING the same service
   * instance and its socket signaling listeners. Used when a fresh guest (re)joins
   * so the host offers from a clean slate (new ICE/DTLS the new peer can match)
   * without re-registering socket handlers (which would storm the negotiation).
   * The caller re-attaches local media (addTracks) and offers afterwards.
   */
  resetConnection () {
    this._clearReconnectTimer()
    if (this.localConnection) {
      try { this.localConnection.close() } catch (e) { /* already closed */ }
      this.localConnection = null
    }
    this.signalingState = 'new'
    this.processedOffers.clear()
    this.processedAnswers.clear()
    this.localIceCandidates = []
    this.pendingIceCandidates = []
    this.isConnected = false

    this.localConnection = new RTCPeerConnection(this._rtcConfig())
    if (this.isHost) this.setupDataChannels()
    this.setupConnectionEventListeners()
  }

  /**
   * Initialize as Guest
   */
  async initializeAsGuest (roomId, socket) {
    console.log('🎯 Initializing WebRTC as Guest for room:', roomId)
    this.isHost = false
    this.roomId = roomId
    this.socket = socket

    try {
      // Create RTCPeerConnection
      this.localConnection = new RTCPeerConnection(this._rtcConfig())

      // Set up connection event listeners
      this.setupConnectionEventListeners()

      // Set up socket signaling

      return true
    } catch (error) {
      console.error('❌ Failed to initialize guest WebRTC:', error)
      return false
    }
  }

  /**
   * Setup data channels (Host only)
   */
  recreateChannels(){
    if(this.canvasChannel?.readyState !== 'open' || this.permissionChannel?.readyState !== 'open' || this.zoomChannel?.readyState !== 'open' || this.dataChannel?.readyState !== 'open'){
      this.setupDataChannels()
    }
  }

  setupDataChannels () {
    if (!this.isHost || !this.localConnection) return

    // Canvas updates channel
    this.canvasChannel = this.localConnection.createDataChannel('canvas', {
      ordered: true
    })

    // Permission updates channel
    this.permissionChannel = this.localConnection.createDataChannel(
      'permissions',
      {
        ordered: true
      }
    )

    // Zoom updates channel
    this.zoomChannel = this.localConnection.createDataChannel('zoom', {
      ordered: true
    })

    // General messaging channel
    this.dataChannel = this.localConnection.createDataChannel('messages', {
      ordered: true
    })

    // Setup channel event listeners
    this.setupChannelEventListeners(this.canvasChannel, 'canvas')
    this.setupChannelEventListeners(this.permissionChannel, 'permissions')
    this.setupChannelEventListeners(this.zoomChannel, 'zoom')
    this.setupChannelEventListeners(this.dataChannel, 'messages')
  }

  /**
   * Setup channel event listeners
   */
  setupChannelEventListeners (channel, channelType) {
    channel.onopen = () => {
      console.log(`📡 ${channelType} channel opened`)
      if (channelType === 'canvas') {
        this.isConnected = true
        this.notifyConnectionStateChange('connected')
        console.log(
          `✅ ${
            this.isHost ? 'Host' : 'Guest'
          } WebRTC connection established via canvas channel`
        )
      }
    }

    channel.onclose = () => {
      console.log(`📡 ${channelType} channel closed`)
      if (channelType === 'canvas') {
        this.isConnected = false
        this.notifyConnectionStateChange('disconnected')
      }
    }

    channel.onmessage = event => {
      this.handleChannelMessage(channelType, event.data)
    }

    channel.onerror = error => {
      console.error(`❌ ${channelType} channel error:`, error)
    }
  }

  /**
   * Setup connection event listeners
   */
  setupConnectionEventListeners () {
    // Handle incoming data channels (Guest side)
    this.localConnection.ondatachannel = event => {
      const channel = event.channel
      console.log(`📡 Received data channel: ${channel.label}`)

      if (channel.label === 'canvas') {
        this.canvasChannel = channel
        this.setupChannelEventListeners(channel, 'canvas')
      } else if (channel.label === 'permissions') {
        this.permissionChannel = channel
        this.setupChannelEventListeners(channel, 'permissions')
      } else if (channel.label === 'zoom') {
        this.zoomChannel = channel
        this.setupChannelEventListeners(channel, 'zoom')
      } else if (channel.label === 'messages') {
        this.dataChannel = channel
        this.setupChannelEventListeners(channel, 'messages')
      }
    }

    // Handle remote track addition
    this.localConnection.ontrack = event => {
      console.log('🎥 Received remote track:', event.track)
      if (this.onTrack) {
        this.onTrack(event.streams.filter(stream => stream.active)[0], event.track)
      }
    }

    // Handle connection state changes. Recovery is driven here and (for earlier
    // detection) in oniceconnectionstatechange below, both funneling into
    // _scheduleRecovery so a drop heals automatically instead of hanging dead.
    this.localConnection.onconnectionstatechange = () => {
      const state = this.localConnection.connectionState
      console.log(`🔗 WebRTC connection state: ${state}`)

      if (state === 'connected') {
        this.isConnected = true
        this.signalingState = 'connected'
        // Recovered — cancel any pending restart and reset the backoff.
        this._clearReconnectTimer()
        this._reconnectAttempts = 0
      } else if (state === 'disconnected') {
        this.isConnected = false
        // A 'disconnected' blip frequently self-heals; give it a grace window
        // before escalating to an ICE-restart (avoids tearing down a link that
        // would have recovered on its own).
        this._scheduleRecovery(4000)
      } else if (state === 'failed') {
        this.isConnected = false
        // 'failed' will not recover on its own — restart immediately.
        this._scheduleRecovery(0)
      } else if (state === 'closed') {
        this.isConnected = false
        this.signalingState = 'new'
        this._clearReconnectTimer()
        this.processedOffers.clear()
        this.processedAnswers.clear()
      }

      this.notifyConnectionStateChange(state)
    }

    // ICE-level failures often surface here before connectionState flips.
    this.localConnection.oniceconnectionstatechange = () => {
      const s = this.localConnection.iceConnectionState
      console.log(`🧊 ICE connection state: ${s}`)
      if (s === 'failed') {
        this._scheduleRecovery(0)
      } else if (s === 'disconnected') {
        this._scheduleRecovery(4000)
      } else if (s === 'connected' || s === 'completed') {
        this._clearReconnectTimer()
      }
    }

    // Handle ICE candidates
    this.localConnection.onicecandidate = event => {
      if (event.candidate) {
        console.log('🧊 Generated ICE candidate')
        this.localIceCandidates.push(event.candidate)

        // Only trickle a standalone candidate once our own remoteDescription is
        // set. Pre-remoteDescription candidates are instead bundled into the
        // offer/answer (see createOffer/handleOffer) and delivered atomically —
        // standalone candidate events over socket.io are NOT buffered, so any
        // emitted before the far peer is subscribed would be silently dropped
        // and ICE would never complete. (Reverts the P0-1 trickle regression.)
        if (this.socket && this.localConnection.remoteDescription) {
          const who = this.isHost ? 'host' : 'peer'
          const candidateData = {
            type: 'webrtc-ice-candidate',
            candidate: event.candidate
          }
          this.socket.emit('peer-info', this.roomId, who, candidateData)
        }

        // If we have a remote peer connection, send the candidate directly
        if (this.onIceCandidate) {
          this.onIceCandidate(event.candidate)
        }
      }
    }
  }

  /**
   * Schedule a recovery attempt after `delayMs`. Coalesces multiple triggers
   * (connectionState + iceConnectionState can both fire) into one timer, and
   * bails out if the connection recovers on its own before it fires.
   */
  _scheduleRecovery (delayMs) {
    if (this._reconnectTimer) return // already pending
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      const cs = this.localConnection && this.localConnection.connectionState
      if (!this.localConnection || cs === 'connected' || cs === 'closed') return
      this._attemptRecovery()
    }, delayMs)
  }

  _clearReconnectTimer () {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
  }

  /**
   * Recover a dropped connection. The host is the sole offerer, so it performs
   * the ICE-restart; the guest cannot create the offer, so it asks the host to
   * renegotiate. This gives the guest a recovery path without a full
   * perfect-negotiation rewrite.
   */
  async _attemptRecovery () {
    this._reconnectAttempts++
    // A renegotiation is a fresh exchange — clear stale dedup/state so the new
    // offer/answer is not rejected as a duplicate.
    this.processedOffers.clear()
    this.processedAnswers.clear()
    this.signalingState = 'new'
    try {
      if (this.isHost) {
        console.log(`🔁 Recovering (attempt ${this._reconnectAttempts}) — host sending ICE-restart offer`)
        await this.createOffer({ iceRestart: true })
      } else {
        console.log(`🔁 Recovering (attempt ${this._reconnectAttempts}) — guest requesting renegotiation`)
        if (this.socket) {
          this.socket.emit('peer-info', this.roomId, 'peer', {
            type: 'webrtc-renegotiate',
            roomId: this.roomId
          })
        }
      }
    } catch (e) {
      console.error('❌ Recovery attempt failed', e)
    }
  }

  /**
   * Resolve when ICE gathering completes, or after `maxMs` as a cap. Replaces the
   * old fixed setTimeout(2000): fast networks resolve early (lower latency), slow
   * networks still get as many candidates as possible bundled into the offer/answer.
   */
  _waitForIceGathering (maxMs = 3000) {
    return new Promise(resolve => {
      const pc = this.localConnection
      if (!pc || pc.iceGatheringState === 'complete') return resolve()
      let done = false
      const finish = () => {
        if (done) return
        done = true
        pc.removeEventListener('icegatheringstatechange', check)
        resolve()
      }
      const check = () => {
        if (pc.iceGatheringState === 'complete') finish()
      }
      pc.addEventListener('icegatheringstatechange', check)
      setTimeout(finish, maxMs)
    })
  }

  sendCameraToggle(state){
    if (!this.socket) {
      console.log('⚠️ No socket available for signaling')
      return
    }

    this.socket.emit('camera-state',this.roomId,this.isHost ? 'host' : 'peer', state )
  }

  sendMicToggle(state){
    if (!this.socket) {
      console.log('⚠️ No socket available for signaling')
      return
    }

    this.socket.emit('mic-state',this.roomId,this.isHost ? 'host' : 'peer', state )
  }

  /**
   * Setup socket signaling for WebRTC
   */
  setupSocketSignaling () {
    if (!this.socket) {
      console.log('⚠️ No socket available for signaling')
      return
    }

    console.log(
      '🔌 Setting up socket signaling for automatic WebRTC connection'
    )

    // Remove any handlers from a previous setup so reconnects don't stack
    // duplicate listeners on the shared socket (each would re-process every
    // offer/candidate). Named handlers let disconnect() remove exactly ours
    // without touching the view's own peer-info listener.
    this._teardownSocketSignaling()

    // Listen for peer-info events that contain WebRTC data
    this._onPeerInfo = async (roomid, who, peerData) => {
      if (roomid !== this.roomId || who === (this.isHost ? 'host' : 'peer'))
        return

      if (peerData && peerData.type === 'webrtc-renegotiate' && this.isHost) {
        // Guest lost its media path and asked us to renegotiate. Send a fresh
        // ICE-restart offer (host is the sole offerer).
        console.log('🔁 Guest requested renegotiation — sending ICE-restart offer')
        this.processedAnswers.clear()
        this.signalingState = 'new'
        await this.createOffer({ iceRestart: true })
      } else if (peerData && peerData.type === 'webrtc-offer' && !this.isHost) {
        // Dedup by the host's monotonic generation id so a retransmit is ignored
        // but a legitimate ICE-restart offer (which may share the same SDP prefix)
        // is NOT dropped. Fall back to an SDP-prefix hash for older payloads.
        const offerKey = peerData.generation != null
          ? `gen-${peerData.generation}`
          : JSON.stringify(peerData.offer.sdp).substring(0, 50)
        if (this.processedOffers.has(offerKey)) {
          console.log('🔄 Skipping duplicate offer')
          return
        }

        // A renegotiation/ICE-restart offer can arrive while we are 'stable'
        // (already connected) — that is expected and allowed. Only bail if we are
        // mid-exchange with our own local offer pending.
        if (this.localConnection.signalingState === 'have-local-offer') {
          console.log(
            `⚠️ Skipping offer - connection in state: ${this.localConnection.signalingState}`
          )
          return
        }

        this.processedOffers.add(offerKey)
        this._answerGeneration = peerData.generation
        this.signalingState = 'offer-received'

        console.log('📥 Received WebRTC offer via peer-info')
        await this.handleOffer(peerData.offer)

        // Add ICE candidates from offer
        for (const candidate of peerData.iceCandidates || []) {
          await this.handleRemoteIceCandidate(candidate)
        }
      } else if (peerData && peerData.type === 'webrtc-answer' && this.isHost) {
        // Dedup by generation (echoed back by the guest) so a re-answer for an
        // ICE-restart is not dropped. Fall back to an SDP-prefix hash.
        const answerKey = peerData.generation != null
          ? `gen-${peerData.generation}`
          : JSON.stringify(peerData.answer.sdp).substring(0, 50)
        if (this.processedAnswers.has(answerKey)) {
          console.log('🔄 Skipping duplicate answer')
          return
        }

        // Check WebRTC connection state
        if (this.localConnection.signalingState !== 'have-local-offer') {
          console.log(
            `⚠️ Skipping answer - connection in state: ${this.localConnection.signalingState}`
          )
          return
        }

        this.processedAnswers.add(answerKey)
        this.signalingState = 'answer-received'

        console.log('📥 Received WebRTC answer via peer-info')
        await this.handleAnswer(peerData.answer)

        // Add ICE candidates from answer
        for (const candidate of peerData.iceCandidates || []) {
          await this.handleRemoteIceCandidate(candidate)
        }
      } else if (peerData && peerData.type === 'webrtc-ice-candidate') {
        console.log('🧊 Received ICE candidate via peer-info')
        await this.handleRemoteIceCandidate(peerData.candidate)
      }
    }

    this._onCameraState = (roomid, who, state) => {
      if (roomid !== this.roomId || who === (this.isHost ? 'host' : 'peer'))
        return
      console.log(`Camera state from ${who} in ${roomid}:`, state)
      if (this.remoteVideoState) this.remoteVideoState(state)
    }

    this._onMicState = (roomid, who, state) => {
      if (roomid !== this.roomId || who === (this.isHost ? 'host' : 'peer'))
        return
      console.log(`Mic state from ${who} in ${roomid}:`, state)
      if (this.remoteAudioState) this.remoteAudioState(state)
    }

    this.socket.on('peer-info', this._onPeerInfo)
    this.socket.on('camera-state', this._onCameraState)
    this.socket.on('mic-state', this._onMicState)
  }

  /**
   * Remove exactly the socket listeners this service registered (by reference),
   * leaving other listeners on the shared socket — e.g. the view's own
   * peer-info handler — intact.
   */
  _teardownSocketSignaling () {
    if (!this.socket) return
    if (this._onPeerInfo) this.socket.off('peer-info', this._onPeerInfo)
    if (this._onCameraState) this.socket.off('camera-state', this._onCameraState)
    if (this._onMicState) this.socket.off('mic-state', this._onMicState)
  }

  /**
   * Create and send offer (Host)
   */
  async createOffer (options = {}) {
    if (!this.isHost || !this.localConnection) {
      console.error('❌ Cannot create offer: not host or no connection')
      return null
    }

    const isRestart = !!options.iceRestart

    // For a normal offer, guard against a duplicate/racing exchange. An
    // ICE-restart offer is intentionally allowed even while 'connected'.
    if (!isRestart) {
      if (
        this.signalingState === 'offer-sent' ||
        this.signalingState === 'connected' || this.localConnection.signalingState !== 'stable'
      ) {
        console.log('⚠️ Offer already sent or connection established')
        this.signalingState = 'new'
      }
    }

    try {
      // Fresh gathering for this offer — drop stale candidates so we only bundle
      // the ones relevant to this (possibly restarted) negotiation.
      this.localIceCandidates = []
      const offer = await this.localConnection.createOffer(isRestart ? { iceRestart: true } : {})
      await this.localConnection.setLocalDescription(offer)
      console.log(`📤 Created and set local offer${isRestart ? ' (ICE restart)' : ''}`)

      this.signalingState = 'offer-sent'
      const generation = ++this._offerGeneration

      // Create connection data object for sharing
      this.connectionData = {
        offer: offer,
        iceCandidates: [],
        roomId: this.roomId
      }

      // Wait for ICE gathering (or a short cap), then send the offer with the
      // candidates BUNDLED IN. Bundling delivers the offer + candidates atomically
      // in a single peer-info event (socket.io preserves order and the receiver
      // loops over `iceCandidates`), which is reliable — unlike standalone trickle
      // events that get dropped if the far peer isn't subscribed yet.
      await this._waitForIceGathering()
      this.connectionData.iceCandidates = [...this.localIceCandidates]
      console.log('📤 Sending WebRTC offer via peer-info event')

      if (this.socket) {
        this.socket.emit('peer-info', this.roomId, 'host', {
          type: 'webrtc-offer',
          offer: this.connectionData.offer,
          iceCandidates: this.connectionData.iceCandidates,
          generation,
          iceRestart: isRestart,
          roomId: this.roomId
        })
      }

      if (this.onOfferReady) {
        this.onOfferReady(this.connectionData)
      }

      return offer
    } catch (error) {
      console.error('❌ Failed to create offer:', error)
      this.signalingState = 'new' // Reset state on error
      return null
    }
  }

  /**
   * Handle remote offer and create answer (Guest)
   */
  async handleOffer (offer) {
    if (this.isHost || !this.localConnection) {
      console.error('❌ Cannot handle offer: is host or no connection')
      return null
    }

    // Check if we already processed an offer or sent an answer
    if (
      this.signalingState === 'answer-sent' ||
      this.signalingState === 'connected' || this.localConnection.signalingState !== 'stable'
    ) {
      console.log('⚠️ Answer already sent or connection established')
      this.signalingState = 'new'
    }

    // Check WebRTC connection state
    // if (this.localConnection.signalingState !== 'stable') {
    //   console.log(
    //     `⚠️ Cannot handle offer - connection in state: ${this.localConnection.signalingState}`
    //   )
    //   return null
    // }

    try {
      // Fresh gathering for this answer (matters on a renegotiation/ICE-restart).
      this.localIceCandidates = []
      await this.localConnection.setRemoteDescription(offer)
      const answer = await this.localConnection.createAnswer()
      await this.localConnection.setLocalDescription(answer)
      console.log('📥 Handled offer and created answer')

      this.signalingState = 'answer-sent'

      // Create answer data object for sharing back to host
      this.connectionData = {
        answer: answer,
        iceCandidates: [],
        roomId: this.roomId
      }

      // Process any pending ICE candidates now that the remote description is set.
      this.processPendingIceCandidates()

      // Wait for ICE gathering (or a short cap), then send the answer with the
      // candidates BUNDLED IN (same reliability reasoning as createOffer above).
      await this._waitForIceGathering()
      this.connectionData.iceCandidates = [...this.localIceCandidates]
      console.log('📤 Sending WebRTC answer via peer-info event')

      if (this.socket) {
        this.socket.emit('peer-info', this.roomId, 'peer', {
          type: 'webrtc-answer',
          answer: this.connectionData.answer,
          iceCandidates: this.connectionData.iceCandidates,
          generation: this._answerGeneration, // echo the offer's generation for dedup
          roomId: this.roomId
        })
      }

      if (this.onAnswerReady) {
        this.onAnswerReady(this.connectionData)
      }

      return answer
    } catch (error) {
      console.error('❌ Failed to handle offer:', error)
      this.signalingState = 'new' // Reset state on error
      return null
    }
  }

  /**
   * Handle remote answer (Host)
   */
  async handleAnswer (answer) {
    if (!this.isHost || !this.localConnection) {
      console.error('❌ Cannot handle answer: not host or no connection')
      return
    }

    // Check WebRTC connection state
    if (this.localConnection.signalingState !== 'have-local-offer') {
      console.log(
        `⚠️ Cannot handle answer - connection in state: ${this.localConnection.signalingState}`
      )
      return
    }

    try {
      await this.localConnection.setRemoteDescription(answer)
      console.log('📥 Handled remote answer')

      this.signalingState = 'answer-received'

      // Process any pending ICE candidates
      this.processPendingIceCandidates()
    } catch (error) {
      console.error('❌ Failed to handle answer:', error)
    }
  }

  /**
   * Handle remote ICE candidate
   */
  async handleRemoteIceCandidate (candidate) {
    if (!this.localConnection) return

    try {
      // If remote description is not set yet, queue the candidate
      if (!this.localConnection.remoteDescription) {
        this.pendingIceCandidates.push(candidate)
        return
      }

      await this.localConnection.addIceCandidate(candidate)
      console.log('🧊 Added remote ICE candidate')
    } catch (error) {
      console.error('❌ Failed to add ICE candidate:', error)
    }
  }

  /**
   * Connect using connection data (Guest connects to Host)
   */
  async connectWithConnectionData (connectionData) {
    if (this.isHost) {
      console.error('❌ Hosts cannot connect with connection data')
      return false
    }

    try {
      console.log('🔗 Connecting with connection data:', connectionData)

      // Handle the offer
      await this.handleOffer(connectionData.offer)

      // Add ICE candidates
      for (const candidate of connectionData.iceCandidates) {
        await this.handleRemoteIceCandidate(candidate)
      }

      console.log('✅ Connected using connection data')
      return true
    } catch (error) {
      console.error('❌ Failed to connect with connection data:', error)
      return false
    }
  }

  /**
   * Complete connection using answer data (Host completes connection)
   */
  async completeConnectionWithAnswer (answerData) {
    if (!this.isHost) {
      console.error('❌ Only hosts can complete connection with answer data')
      return false
    }

    try {
      console.log('🔗 Completing connection with answer data:', answerData)

      // Handle the answer
      await this.handleAnswer(answerData.answer)

      // Add ICE candidates
      for (const candidate of answerData.iceCandidates) {
        await this.handleRemoteIceCandidate(candidate)
      }

      console.log('✅ Connection completed with answer data')
      return true
    } catch (error) {
      console.error('❌ Failed to complete connection with answer data:', error)
      return false
    }
  }

  /**
   * Get connection data for sharing
   */
  getConnectionData () {
    return this.connectionData
  }

  /**
   * Process pending ICE candidates
   */
  async processPendingIceCandidates () {
    if (
      !this.localConnection.remoteDescription ||
      this.pendingIceCandidates.length === 0
    ) {
      return
    }

    console.log(
      `🧊 Processing ${this.pendingIceCandidates.length} pending ICE candidates`
    )

    for (const candidate of this.pendingIceCandidates) {
      try {
        await this.localConnection.addIceCandidate(candidate)
      } catch (error) {
        console.error('❌ Failed to add pending ICE candidate:', error)
      }
    }

    this.pendingIceCandidates = []
  }

  /**
   * Chunk sender that respects the data-channel buffer (backpressure).
   * Yields to the event loop via `bufferedamountlow` when the SCTP buffer fills,
   * instead of blasting hundreds of synchronous sends that overflow it (P0-3).
   */
  async _sendChunked (channel, chunks) {
    const HIGH = 1 << 20 // 1 MB high-water mark
    channel.bufferedAmountLowThreshold = 256 * 1024
    for (const message of chunks) {
      if (channel.bufferedAmount > HIGH) {
        await new Promise(res => {
          const on = () => {
            channel.removeEventListener('bufferedamountlow', on)
            res()
          }
          channel.addEventListener('bufferedamountlow', on)
        })
      }
      channel.send(message)
    }
  }

  /**
   * Send canvas update
   */
  async sendCanvasUpdate (canvasData) {
    if (!this.canvasChannel || this.canvasChannel.readyState !== 'open') {
      console.warn('⚠️ Canvas channel not ready')
      // return false
    }

    try {
      if (canvasData.backgroundImage && canvasData.backgroundImage.length > 16000) {

        const chunkSize = 16000
        const totalChunks = Math.ceil(canvasData.backgroundImage.length / chunkSize)
        // Group all chunks of the same image so the receiver can reassemble
        // them safely even if a second transfer interleaves (P0-3).
        const transferId = `${Date.now()}-${Math.random().toString(36).slice(2)}`

        const chunks = []
        for (let i = 0; i < totalChunks; i++) {
          const chunk = canvasData.backgroundImage.substring(i * chunkSize, (i + 1) * chunkSize)
          chunks.push(JSON.stringify({
            type: 'canvas_update',
            data: {
              ...canvasData,
              backgroundImage: {
                chunk,
                i,
                totalChunks,
                transferId
              }
            },
            timestamp: Date.now()
          }))
        }

        await this._sendChunked(this.canvasChannel, chunks)
        console.log('📤 Sent canvas update in', totalChunks, 'chunks')
        return true
      } else {

        const message = JSON.stringify({
          type: 'canvas_update',
          data: canvasData,
          timestamp: Date.now()
        })

        this.canvasChannel.send(message)
        console.log('📤 Sent canvas update')
        return true
      }
    } catch (error) {
      console.error('❌ Failed to send canvas update:', error)
      return false
    }
  }

  /**
   * Send permission update (Host only)
   */
  sendPermissionUpdate (permissions) {
    if (
      !this.isHost ||
      !this.permissionChannel ||
      this.permissionChannel.readyState !== 'open'
    ) {
      console.warn('⚠️ Permission channel not ready or not host')
      return false
    }

    try {
      const message = JSON.stringify({
        type: 'permission_update',
        data: permissions,
        timestamp: Date.now()
      })

      this.permissionChannel.send(message)
      console.log('📤 Sent permission update:', permissions)
      return true
    } catch (error) {
      console.error('❌ Failed to send permission update:', error)
      return false
    }
  }

  /**
   * Send zoom update (Host only)
   */
  sendZoomUpdate (zoomData) {
    if (
      !this.isHost ||
      !this.zoomChannel ||
      this.zoomChannel.readyState !== 'open'
    ) {
      console.warn('⚠️ Zoom channel not ready or not host')
      return false
    }

    try {
      const message = JSON.stringify({
        type: 'zoom_update',
        data: zoomData,
        timestamp: Date.now()
      })

      this.zoomChannel.send(message)
      console.log('📤 Sent zoom update:', zoomData)
      return true
    } catch (error) {
      console.error('❌ Failed to send zoom update:', error)
      return false
    }
  }

  /**
   * Send general message
   */
  sendMessage (messageData) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      console.warn('⚠️ Data channel not ready')
      return false
    }

    try {
      // Always wrap the message in the expected structure
      const message = JSON.stringify({
        type: 'message',
        data: messageData,
        timestamp: Date.now()
      })

      this.dataChannel.send(message)
      console.log('📤 Sent message:', typeof messageData === 'string' ? 'text' : 'object')
      return true
    } catch (error) {
      console.error('❌ Failed to send message:', error)
      return false
    }
  }

  /**
   * Handle incoming channel messages
   */
  handleChannelMessage (channelType, messageData) {
    try {
      // const message = JSON.parse(messageData)
      let message;
      if(typeof messageData === 'string' && messageData.includes('{'))
        message = JSON.parse(messageData)
      else message = { type: channelType, data: messageData }
      switch (channelType) {
        case 'canvas':
          if (message.type === 'canvas_update' && this.onCanvasUpdate) {
            this.onCanvasUpdate(message.data)
          }
          break
        case 'permissions':
          if (message.type === 'permission_update' && this.onPermissionUpdate) {
            this.onPermissionUpdate(message.data)
          }
          break
        case 'zoom':
          if (message.type === 'zoom_update' && this.onZoomUpdate) {
            this.onZoomUpdate(message.data)
          }
          break
        case 'messages':
          if (message.type === 'message' && this.onMessage) {
            // For session termination and other special messages, pass the full data
            // For regular messages, pass just the content
            if (typeof message.data === 'object' && message.data.type) {
              console.log('📨 Special message received:', message.data.type);
              this.onMessage(message.data)
            } else {
              console.log('📨 Regular message received');
              this.onMessage(message.data)
            }
          }
          break
      }
    } catch (error) {
      console.error('❌ Failed to parse channel message:', error)
    }
  }

  /**
   * Notify connection state change
   */
  notifyConnectionStateChange (state) {
    if (this.onConnectionStateChange) {
      this.onConnectionStateChange(state)
    }
  }

  /**
   * Cleanup and close connections
   */
  disconnect () {
    console.log('🔌 Disconnecting WebRTC...')

    // Cancel any pending auto-recovery — this teardown is intentional.
    this._clearReconnectTimer()
    // Remove our socket listeners so they don't leak / double-fire after a re-init.
    this._teardownSocketSignaling()

    // Reset signaling state
    this.signalingState = 'new'
    this.processedOffers.clear()
    this.processedAnswers.clear()

    // Close data channels
    if (this.canvasChannel) {
      this.canvasChannel.close()
      this.canvasChannel = null
    }

    if (this.permissionChannel) {
      this.permissionChannel.close()
      this.permissionChannel = null
    }

    if (this.zoomChannel) {
      this.zoomChannel.close()
      this.zoomChannel = null
    }

    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }

    // Close peer connection
    if (this.localConnection) {
      this.localConnection.close()
      this.localConnection = null
    }

    this.isConnected = false
    this.notifyConnectionStateChange('disconnected')
    console.log('✅ WebRTC disconnected')
  }

  /**
   * Get connection status
   */
  getStatus () {
    return {
      isConnected: this.isConnected,
      isHost: this.isHost,
      roomId: this.roomId,
      connectionState: this.localConnection
        ? this.localConnection.connectionState
        : 'closed'
    }
  }
}

export default WebRTCService
